import { css } from "@emotion/css";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";

import { downloadPdfFromResponse } from "./downloadPdfFromResponse";
import { PdfCanvasPreview } from "./PdfCanvasPreview";
import { Checkbox } from "../../components/inputs/checkbox/Checkbox";
import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";
import { Link } from "../../components/link/Link";
import { Stack } from "../../foundations/grid-layout/Stack";
import { Text } from "../../foundations/typography/Text";
import { Loader } from "../../components/loaders/loading/Loader";
import { useColors } from "../../hooks/useColors";

/**
 * ContractPdfScroll - A clickwrap PDF viewer component.
 *
 * Renders a PDF document in-page (PDF.js canvases) with scroll-to-accept
 * behavior: the user must scroll to the bottom of the document before the
 * acceptance checkbox is shown, exactly like ContractScroll for HTML contracts.
 *
 * Styled to match the ESignDocument component appearance (bordered section
 * with toolbar header, scrollable content area, and controls footer).
 */
export function ContractPdfScroll({
  url,
  onDownload,
  downloadFilename,
  onSubmit,
  errorMessage,
  style,
  label,
}: {
  /** Direct PDF URL used only when onDownload is omitted. */
  url: string;
  /**
   * Called when download is requested. Should return a Response object with the PDF blob.
   * Also used to fetch the PDF for display when url is empty.
   * If not provided, a direct link to the URL is shown instead.
   */
  onDownload?: () => Promise<Response>;
  /**
   * Custom filename for the downloaded file. If provided, this will be used instead of
   * the filename from the Content-Disposition header. A .pdf extension will be appended
   * if not already present.
   */
  downloadFilename?: string;
  /** Called when the user accepts the document by checking the checkbox */
  onSubmit?: () => void | Promise<void>;
  /** Validation error message to display below the component */
  errorMessage?: string;
  /** Container style overrides */
  style?: React.CSSProperties;
  /** Custom label for the acceptance checkbox */
  label?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const styles = useContractPdfScrollStyles();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const [hasToScroll, setHasToScroll] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const onDownloadRef = useRef(onDownload);
  onDownloadRef.current = onDownload;

  // Prefer onDownload for in-page preview. Template-builder PDFs are stored
  // behind a presigned S3 URL that PDF.js cannot fetch from the applicant
  // origin (CORS / connect-src). The authenticated preview API returns bytes
  // we can turn into a blob: URL.
  useEffect(() => {
    const downloadPdf = onDownloadRef.current;
    if (!downloadPdf) return;

    setFetchError(false);
    setIsPdfLoaded(false);
    setHasScrolledToBottom(false);
    setHasToScroll(false);

    let revoked = false;
    const fetchPdf = async () => {
      try {
        const response = await downloadPdf();
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (!revoked) {
          setBlobUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (e) {
        console.error("Error fetching PDF for display", e);
        if (!revoked) {
          setFetchError(true);
        }
      }
    };

    fetchPdf();

    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [url]);

  const displayUrl = onDownload ? blobUrl : url || null;
  // Presigned template URLs are regional S3 hosts. PDF.js fetches them
  // (connect-src) and an iframe navigates them (frame-src); neither directive
  // allows those hosts. object-src does, so embed the URL directly.
  const embedRemotePdf = !onDownload && /^https?:\/\//i.test(url);
  const previousDisplayUrlRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (previousDisplayUrlRef.current === undefined) {
      previousDisplayUrlRef.current = displayUrl;
      return;
    }
    if (previousDisplayUrlRef.current === displayUrl) {
      return;
    }
    previousDisplayUrlRef.current = displayUrl;
    setFetchError(false);
    setIsPdfLoaded(false);
    setHasScrolledToBottom(false);
    setHasToScroll(false);
  }, [displayUrl]);

  // Check if the content wrapper div is scrollable.
  // If the preview does not overflow, the checkbox is shown immediately after
  // the PDF loads.
  const checkScrollability = useCallback(() => {
    if (!contentRef.current || !isPdfLoaded) return;

    const element = contentRef.current;
    const hasScrollableContent = element.scrollHeight > element.clientHeight;

    if (!hasScrollableContent) {
      setHasScrolledToBottom(true);
      setHasToScroll(false);
      return;
    }

    // Re-measure every time. A width re-render can briefly look unscrolled;
    // keeping a previous "reached the bottom" flag would leave the checkbox
    // visible after the pages overflow again.
    const atBottom =
      element.scrollHeight <= element.scrollTop + element.clientHeight + 50;
    setHasScrolledToBottom(atBottom);
    setHasToScroll(!atBottom);
  }, [isPdfLoaded]);

  const handlePdfLoad = useCallback(() => {
    setFetchError(false);
    setIsPdfLoaded(true);
    // Delay check to allow layout to settle
    setTimeout(checkScrollability, 500);
  }, [checkScrollability]);

  const handleEmbeddedPdfError = useCallback(() => {
    setFetchError(true);
    setIsPdfLoaded(false);
    setHasToScroll(false);
    setHasScrolledToBottom(false);
  }, []);

  // React registers load on <object>, but not error. Attach the listener
  // when the element mounts so a blocked or expired presigned URL can fail.
  const setEmbeddedPdfNode = useCallback(
    (node: HTMLObjectElement | null) => {
      if (!node) return;

      node.addEventListener("error", handleEmbeddedPdfError);
      return () => node.removeEventListener("error", handleEmbeddedPdfError);
    },
    [handleEmbeddedPdfError]
  );

  // Handle scroll on the content wrapper
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!isPdfLoaded || hasScrolledToBottom) return;

      const element = e.target as HTMLDivElement;
      if (
        element.scrollHeight <=
        element.scrollTop + element.clientHeight + 50
      ) {
        setHasScrolledToBottom(true);
        setHasToScroll(false);
      }
    },
    [isPdfLoaded, hasScrolledToBottom]
  );

  // Re-check scrollability on resize
  useEffect(() => {
    if (!contentRef.current || !isPdfLoaded) return;

    const debouncedCheck = debounce(checkScrollability, 200);
    const observer = new ResizeObserver(debouncedCheck);
    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
      debouncedCheck.cancel();
    };
  }, [checkScrollability, isPdfLoaded]);

  // Handle download with loading state and blob handling
  const handleDownloadClick = useCallback(async () => {
    if (!onDownload || isDownloading) return;

    setIsDownloading(true);
    setDownloadError(false);
    try {
      const response = await onDownload();

      await downloadPdfFromResponse(response, downloadFilename);
    } catch (e) {
      console.error("Error downloading document", e);
      setDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, isDownloading, downloadFilename]);

  // Handle checkbox accept
  const handleAccept = useCallback(async () => {
    if (isAgreed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit();
      }
      setIsAgreed(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [isAgreed, isSubmitting, onSubmit]);

  const defaultLabel = <Trans>I understand and agree to this document</Trans>;
  const checkboxLabel = label ?? defaultLabel;

  return (
    <div className={styles.container} style={style}>
      <section
        className={css(styles.section, {
          borderColor: errorMessage ? "var(--border-danger-input)" : undefined,
        })}
      >
        {/* Header/Toolbar */}
        <header className={styles.toolbar}>
          {onDownload ? (
            <Link
              testID="pdf-scroll-download-link"
              onPress={isDownloading ? undefined : handleDownloadClick}
              textColor="text-weak"
              textStyle="global/body-secondary"
              style={{
                opacity: isDownloading ? 0.5 : 1,
                cursor: isDownloading ? "wait" : "pointer",
              }}
            >
              {isDownloading ? (
                <Trans>Downloading...</Trans>
              ) : (
                <Trans>Download</Trans>
              )}
            </Link>
          ) : displayUrl ? (
            <Link
              testID="pdf-scroll-download-link"
              href={displayUrl}
              target="_blank"
              textColor="text-weak"
              textStyle="global/body-secondary"
            >
              <Trans>Download</Trans>
            </Link>
          ) : null}
        </header>

        {/* PDF Content */}
        <div
          ref={contentRef}
          className={styles.content}
          onScroll={handleScroll}
          aria-label="pdf-document-content"
          role="document"
          tabIndex={0}
        >
          {fetchError ? (
            <div className={styles.loadingOverlay}>
              <Stack
                gap="space-4"
                align="center"
                justify="center"
                style={{ height: "100%" }}
              >
                <Text color="text-danger" testID="pdf-scroll-load-error">
                  <Trans>Failed to load document</Trans>
                </Text>
              </Stack>
            </div>
          ) : (
            !isPdfLoaded &&
            !embedRemotePdf && (
              <div className={styles.loadingOverlay}>
                <Stack
                  gap="space-4"
                  align="center"
                  justify="center"
                  style={{ height: "100%" }}
                >
                  <Loader type="dots" />
                  <Text>
                    <Trans>Loading document...</Trans>
                  </Text>
                </Stack>
              </div>
            )
          )}
          {displayUrl && !fetchError && embedRemotePdf && (
            <object
              ref={setEmbeddedPdfNode}
              data={displayUrl}
              type="application/pdf"
              className={styles.embeddedPdf}
              aria-label={t("PDF Document")}
              data-testid="pdf-scroll-preview"
              onLoad={handlePdfLoad}
            >
              {/* Kept mounted for the life of the object. Browsers show this
                  when the PDF plugin cannot render, often without an error
                  event, and replacing these children can blank a loaded PDF. */}
              <div
                className={styles.embeddedPdfFallback}
                data-testid="pdf-scroll-embedded-fallback"
              >
                <Text color="text-danger">
                  <Trans>Failed to load document</Trans>
                </Text>
              </div>
            </object>
          )}
          {displayUrl && !fetchError && !embedRemotePdf && (
            <PdfCanvasPreview
              src={displayUrl}
              title={t("PDF Document")}
              testID="pdf-scroll-preview"
              onLoad={handlePdfLoad}
              onError={() => setFetchError(true)}
            />
          )}
        </div>

        {/* Footer/Controls */}
        <footer className={styles.controls}>
          {isPdfLoaded &&
            onSubmit &&
            (isAgreed ? (
              <div className={styles.checkboxWrapper}>
                <Checkbox
                  isSelected={true}
                  isDisabled={true}
                  testID="pdf-scroll-checkbox-agreed"
                >
                  {checkboxLabel}
                </Checkbox>
              </div>
            ) : hasToScroll ? (
              <div className={styles.scrollMessage}>
                <Text testID="pdf-scroll-please-scroll-text" align="center">
                  <Trans>Please scroll to the bottom to accept</Trans>
                </Text>
              </div>
            ) : (
              <div className={styles.checkboxWrapper}>
                <Checkbox
                  isSelected={false}
                  onChange={handleAccept}
                  isDisabled={isSubmitting}
                  testID="pdf-scroll-checkbox"
                >
                  {checkboxLabel}
                </Checkbox>
              </div>
            ))}
        </footer>
      </section>
      {downloadError && (
        <Text color="text-danger" testID="pdf-scroll-download-error">
          <Trans>Failed to download document</Trans>
        </Text>
      )}
      {errorMessage && (
        <FormFeedback type="invalid">{errorMessage}</FormFeedback>
      )}
    </div>
  );
}

/** Ten steps of space-12 (48px) so the embedded PDF stays readable. */
const embeddedPdfMinHeight = "calc(var(--space-12) * 10)";

function useContractPdfScrollStyles() {
  const colors = useColors();

  return {
    container: css({
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }),
    section: css({
      backgroundColor: "var(--background-base)",
      width: "100%",
      minHeight: "322px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${colors["border-base"]}`,
      borderRadius: "var(--border-radius-3)",
      overflow: "hidden",
    }),
    toolbar: css({
      display: "flex",
      justifyContent: "flex-end",
      padding: "var(--space-3) var(--space-4)",
      borderBottom: `1px solid ${colors["border-base"]}`,
      backgroundColor: "var(--background-base)",
      flexShrink: 0,
    }),
    content: css({
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      backgroundColor: colors["surface-sunken"],
      position: "relative",
    }),
    embeddedPdf: css({
      display: "block",
      width: "100%",
      height: "100%",
      minHeight: embeddedPdfMinHeight,
      border: "none",
    }),
    embeddedPdfFallback: css({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: embeddedPdfMinHeight,
    }),
    loadingOverlay: css({
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors["surface-sunken"],
      zIndex: 1,
    }),
    controls: css({
      padding: "var(--space-3) var(--space-4)",
      backgroundColor: "var(--background-base)",
      borderTop: `1px solid ${colors["border-base"]}`,
      flexShrink: 0,
    }),
    scrollMessage: css({
      width: "100%",
      textAlign: "center",
    }),
    checkboxWrapper: css({
      width: "100%",
      display: "flex",
      justifyContent: "flex-start",
    }),
  };
}
